"use client";

import { MdKeyboardArrowDown, MdClose } from "react-icons/md";
import { HiMenuAlt3 } from "react-icons/hi";
import { useState, useEffect, useRef } from "react";
import FlotsIconDark from "@/public/FlotsIconDark.svg";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { FaUser, FaBuilding, FaArrowRight } from "react-icons/fa6";
import { FormattedMessage, useIntl } from "react-intl";
import LanguageSwitcher from "./LanguageSwitcher";
import { trackCtaClick, trackEvent } from "@/app/providers/ObservabilityProvider";
import { useAppLink } from "@/lib/analytics/app-links";
import { CATEGORY_META, CATEGORIES } from "@/app/components/root/Ressources/category-meta";
import { categoryToSlug } from "@/lib/category-slugs";
import { homePath, isHomePath } from "@/lib/locale-routing";
import { navigateToProfileSection, navigateToSection } from "./profileNav";
import { useLocale } from "@/app/providers/IntlProvider";
import { useFocusTrap } from "@/app/hooks/useFocusTrap";

/**
 * Where the mobile app bar sits, and whether the page has moved under it. On phones and
 * tablets the promo strip scrolls away with the page, so the bar starts right under it and
 * rises with it until it reaches the top of the screen. rAF-throttled passive listener.
 *
 * @returns `top` in px (null before mount, when the CSS fallback places the bar) and
 * `scrolled` (the page has moved: the bar takes its frosted ground).
 */
function useBarPosition() {
  const [state, setState] = useState<{ top: number | null; scrolled: boolean }>({ top: null, scrolled: false });
  useEffect(() => {
    let frame = 0;
    const measure = () => {
      frame = 0;
      const promo = document.querySelector<HTMLElement>(".promo-banner");
      const promoH = promo && getComputedStyle(promo).display !== "none" ? promo.offsetHeight : 0;
      const y = window.scrollY;
      const top = Math.max(0, promoH - y);
      const scrolled = y > 8;
      setState((p) => (p.top === top && p.scrolled === scrolled ? p : { top, scrolled }));
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(measure);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    // Dismissing the promo strip changes the bar's place without any scroll.
    const mo = new MutationObserver(onScroll);
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ["data-promo-dismissed"] });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      mo.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);
  return state;
}

/**
 * Whether the app bar shows its sign-up pill: always, except on the home page while the
 * hero's own sign-up button (`[data-hero-cta]`) is on screen, so the first screen never
 * shows the same call to action twice.
 *
 * @param currentRoute - Current pathname.
 */
function useBarCtaVisible(currentRoute: string) {
  const home = isHomePath(currentRoute);
  const [heroCtaOnScreen, setHeroCtaOnScreen] = useState(true);
  useEffect(() => {
    if (!home) return;
    const el = document.querySelector("[data-hero-cta]");
    if (!el) return;
    // The bar covers the top 56 px: a button hidden under it counts as gone.
    const io = new IntersectionObserver(([e]) => setHeroCtaOnScreen(e.isIntersecting), { rootMargin: "-56px 0px 0px 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, [home]);
  return !home || !heroCtaOnScreen;
}

const MobileNavbar = ({ currentRoute }: { currentRoute: string }) => {
  const intl = useIntl();
  const router = useRouter();
  const { locale } = useLocale();
  const ressourcesSuffix = locale === "en" ? "/en" : "";
  const [isOpen, setIsOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<null | "features" | "ressources">(null);
  const [openSubDropdown, setOpenSubDropdown] = useState<null | "indiv" | "org">(null);
  const { href: appUrl, target: appTarget } = useAppLink("navbar-mobile");

  const dispatchProfileNav = (profile: "Individual" | "Organization", scrollTo: string) =>
    navigateToProfileSection(currentRoute, router, profile, scrollTo, locale);

  const dispatchSectionNav = (profile: "Individual" | "Organization", scrollTo: string) =>
    navigateToSection(currentRoute, router, profile, scrollTo, locale);

  const closeRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLElement>(null);
  // Modal drawer: Tab cycles inside it, and focus returns to the menu button on close.
  useFocusTrap(drawerRef, isOpen);
  // Focus moves into the drawer when it opens (it used to grab focus on every page load).
  const openMenu = () => {
    setIsOpen(true);
    document.body.style.overflow = "hidden";
    requestAnimationFrame(() => closeRef.current?.focus());
  };
  const closeMenu = () => { setIsOpen(false); document.body.style.overflow = "auto"; setOpenDropdown(null); setOpenSubDropdown(null); };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => { if (e.key === "Escape" && isOpen) closeMenu(); };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  const { top, scrolled } = useBarPosition();
  const showCta = useBarCtaVisible(currentRoute);
  const { href: barAppUrl, target: barAppTarget } = useAppLink("navbar-mobile-bar");

  return (
    <>
      {/* Slim app bar (phones and tablets): the brand, a short sign-up pill once the hero's
          own button has scrolled away, and the menu. Transparent over the hero, frosted white
          once the page moves, so text scrolling under it never collides with a floating
          button. It follows the promo strip up as it scrolls away, then stays at the top. */}
      <nav
        aria-label={intl.formatMessage({ id: "a11y.mainNav" })}
        className={`fixed inset-x-0 z-50 flex h-14 items-center gap-2 pl-[max(1rem,env(safe-area-inset-left))] pr-[max(0.75rem,env(safe-area-inset-right))] transition-[background-color,box-shadow] duration-300 ${
          scrolled ? "bg-surface/90 shadow-[0_1px_0_0_var(--color-blue-light)] backdrop-blur-md" : "bg-transparent"
        }`}
        style={{ top: top === null ? "var(--promo-h, 0px)" : `${top}px` }}
      >
        <Link
          href={homePath(locale)}
          onClick={(e) => { if (isHomePath(currentRoute)) { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); } }}
          className="flex min-h-11 items-center gap-2 rounded-lg text-title-blue"
          aria-label={intl.formatMessage({ id: "a11y.brandHome" })}
        >
          <FlotsIconDark className="logo-mark h-7 w-7" aria-hidden="true" />
          <span className="text-lg font-bold italic" aria-hidden="true">flots</span>
        </Link>
        <div className="ml-auto flex items-center gap-1.5">
          <Link
            href={barAppUrl}
            onClickCapture={() => { trackEvent("navbar-bar-cta-button", {}, { criticalOnNav: true }); trackCtaClick("navbar-mobile-bar", barAppTarget); }}
            aria-hidden={!showCta}
            tabIndex={showCta ? undefined : -1}
            className={`flex min-h-11 items-center rounded-full bg-blue-main px-4 text-sm font-semibold text-white shadow-md shadow-blue-main/25 transition duration-300 hover:bg-blue-fill motion-reduce:transition-none ${
              showCta ? "opacity-100" : "pointer-events-none translate-y-1 opacity-0"
            }`}
          >
            <FormattedMessage id="nav.ctaShort" />
          </Link>
          <button
            type="button"
            aria-label={intl.formatMessage({
              id: isOpen ? "a11y.menuClose" : "a11y.menuOpen",
            })}
            aria-expanded={isOpen}
            aria-controls="mobile-nav-drawer"
            className="flex h-11 w-11 cursor-pointer items-center justify-center rounded-xl text-title-blue transition-colors hover:bg-blue-ulight"
            onClick={openMenu}
          >
            <HiMenuAlt3 aria-hidden="true" className="text-2xl" />
          </button>
        </div>
      </nav>

      {isOpen && <div aria-hidden="true" className="fixed inset-0 z-50 bg-black/60" onClick={closeMenu} />}

      <aside
        ref={drawerRef}
        id="mobile-nav-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={intl.formatMessage({ id: "a11y.navigationMenu" })}
        // Closed: off-screen, shadowless (its shadow used to paint a grey band down the right
        // edge of every page), hidden once the slide-out ends and inert, so neither keyboard
        // nor screen readers reach it.
        inert={!isOpen}
        className={`fixed top-0 right-0 z-50 h-full w-[85%] max-w-sm bg-surface flex flex-col transition-[translate,box-shadow,visibility] duration-300 ease-in-out ${
          isOpen ? "visible translate-x-0 shadow-2xl" : "invisible translate-x-full shadow-none"
        }`}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <Link
            href={homePath(locale)}
            onClick={(e) => { closeMenu(); if (isHomePath(currentRoute)) { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); } }}
            className="flex min-h-11 items-center gap-2 text-slate-800"
            aria-label={intl.formatMessage({ id: "a11y.brandHome" })}
          >
            <FlotsIconDark className="logo-mark w-7 h-7" aria-hidden="true" />
            <span className="text-lg font-bold italic" aria-hidden="true">flots</span>
          </Link>
          <button
            type="button"
            aria-label={intl.formatMessage({ id: "a11y.menuClose" })}
            onClick={closeMenu}
            ref={closeRef}
            className="w-11 h-11 flex items-center justify-center rounded-lg text-slate-600 hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <MdClose aria-hidden="true" className="text-xl" />
          </button>
        </div>

        <ul className="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-1">

          {/* Features */}
          <li>
            <button
              onClick={() => setOpenDropdown(openDropdown === "features" ? null : "features")}
              aria-expanded={openDropdown === "features"}
              aria-controls="mobile-features-dropdown"
              className="flex items-center justify-between w-full px-4 py-3 rounded-xl text-slate-700 font-medium hover:bg-gray-50 transition-colors cursor-pointer"
            >
              <FormattedMessage id="nav.features" />
              <MdKeyboardArrowDown aria-hidden="true" className={`text-lg text-gray-400 transition-transform duration-200 ${openDropdown === "features" ? "rotate-180" : ""}`} />
            </button>
            <div id="mobile-features-dropdown" className={`overflow-hidden transition-all duration-250 ease-in-out ${openDropdown === "features" ? "max-h-150" : "max-h-0"}`}>
              <div className="ml-2 mt-1 flex flex-col gap-1">
                <div>
                  <button
                    onClick={() => setOpenSubDropdown(openSubDropdown === "indiv" ? null : "indiv")}
                    aria-expanded={openSubDropdown === "indiv"}
                    aria-controls="mobile-indiv-submenu"
                    className="flex items-center gap-2 w-full px-4 py-2.5 rounded-lg hover:bg-blue-ulight transition-colors cursor-pointer"
                  >
                    <FaUser aria-hidden="true" className="text-brand text-xs shrink-0" />
                    <span className="text-sm font-semibold text-brand flex-1 text-left"><FormattedMessage id="nav.features.indiv.label" /></span>
                    <MdKeyboardArrowDown aria-hidden="true" className={`text-base text-brand/60 transition-transform duration-200 ${openSubDropdown === "indiv" ? "rotate-180" : ""}`} />
                  </button>
                  <div id="mobile-indiv-submenu" className={`overflow-hidden transition-all duration-200 ${openSubDropdown === "indiv" ? "max-h-96" : "max-h-0"}`}>
                    <ul className="ml-4 flex flex-col gap-0.5 mt-1 mb-2">
                      {[
                        { titleKey: "nav.story.tasks.title", descKey: "nav.story.tasks.desc", scrollTo: "story-build" },
                        { titleKey: "nav.story.notes.title", descKey: "nav.story.notes.desc", scrollTo: "story-action" },
                        { titleKey: "nav.story.planday.title", descKey: "nav.story.planday.desc", scrollTo: "profile-planday" },
                      ].map((item) => (
                        <li key={item.scrollTo}>
                          <button onClick={() => { trackEvent("navbar-feature-item", { scrollTo: item.scrollTo }); dispatchProfileNav("Individual", item.scrollTo); closeMenu(); }} className="w-full text-left px-3 py-2 rounded-lg hover:bg-blue-ulight transition-colors cursor-pointer">
                            <p className="text-sm font-medium text-slate-700"><FormattedMessage id={item.titleKey} /></p>
                            <p className="text-xs text-gray-main mt-0.5"><FormattedMessage id={item.descKey} /></p>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div>
                  <button
                    onClick={() => setOpenSubDropdown(openSubDropdown === "org" ? null : "org")}
                    aria-expanded={openSubDropdown === "org"}
                    aria-controls="mobile-org-submenu"
                    className="flex items-center gap-2 w-full px-4 py-2.5 rounded-lg hover:bg-blue-ulight transition-colors cursor-pointer"
                  >
                    <FaBuilding aria-hidden="true" className="text-brand text-xs shrink-0" />
                    <span className="text-sm font-semibold text-brand flex-1 text-left"><FormattedMessage id="nav.features.org.label" /></span>
                    <MdKeyboardArrowDown aria-hidden="true" className={`text-base text-brand/60 transition-transform duration-200 ${openSubDropdown === "org" ? "rotate-180" : ""}`} />
                  </button>
                  <div id="mobile-org-submenu" className={`overflow-hidden transition-all duration-200 ${openSubDropdown === "org" ? "max-h-64" : "max-h-0"}`}>
                    <ul className="ml-4 flex flex-col gap-0.5 mt-1 mb-2">
                      {[
                        { titleKey: "nav.story.privacy.title", descKey: "nav.story.privacy.desc", scrollTo: "profile-privacy" },
                        { titleKey: "nav.story.delegate.title", descKey: "nav.story.delegate.desc", scrollTo: "profile-delegate" },
                        { titleKey: "nav.story.live.title", descKey: "nav.story.live.desc", scrollTo: "profile-live" },
                      ].map((item) => (
                        <li key={item.scrollTo}>
                          <button onClick={() => { trackEvent("navbar-feature-item", { scrollTo: item.scrollTo }); dispatchProfileNav("Organization", item.scrollTo); closeMenu(); }} className="w-full text-left px-3 py-2 rounded-lg hover:bg-blue-ulight transition-colors cursor-pointer">
                            <p className="text-sm font-medium text-slate-700"><FormattedMessage id={item.titleKey} /></p>
                            <p className="text-xs text-gray-main mt-0.5"><FormattedMessage id={item.descKey} /></p>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </li>

          <li className="h-px bg-gray-100 mx-2 my-1" />

          <li>
            <button onClick={() => { trackEvent("navbar-pricing-link"); dispatchSectionNav("Individual", "pricing"); closeMenu(); }} className="flex items-center w-full px-4 py-3 rounded-xl text-slate-700 font-medium hover:bg-gray-50 transition-colors cursor-pointer">
              <FormattedMessage id="nav.pricing" />
            </button>
          </li>

          <li>
            <button onClick={() => { trackEvent("navbar-contact-link"); dispatchSectionNav("Individual", "contact"); closeMenu(); }} className="flex items-center w-full px-4 py-3 rounded-xl text-slate-700 font-medium hover:bg-gray-50 transition-colors cursor-pointer">
              <FormattedMessage id="nav.contact" />
            </button>
          </li>

          {/* Ressources — label navigates to /ressources, chevron opens accordion */}
          <li>
            <div className="flex items-center rounded-xl hover:bg-gray-50 transition-colors">
              <Link href={`/resources${ressourcesSuffix}`} onClick={closeMenu} className="flex-1 px-4 py-3 text-slate-700 font-medium">
                <FormattedMessage id="nav.ressources" />
              </Link>
              <button
                onClick={() => setOpenDropdown(openDropdown === "ressources" ? null : "ressources")}
                aria-expanded={openDropdown === "ressources"}
                aria-controls="mobile-ressources-dropdown"
                aria-label={intl.formatMessage({ id: "a11y.ressourcesCategories" })}
                className="flex items-center justify-center px-4 py-3 cursor-pointer"
              >
                <MdKeyboardArrowDown aria-hidden="true" className={`text-lg text-gray-400 transition-transform duration-200 ${openDropdown === "ressources" ? "rotate-180" : ""}`} />
              </button>
            </div>
            <div id="mobile-ressources-dropdown" className={`overflow-hidden transition-all duration-250 ease-in-out ${openDropdown === "ressources" ? "max-h-[32rem]" : "max-h-0"}`}>
              <ul className="ml-2 mt-1 flex flex-col gap-0.5">
                {CATEGORIES.map((category) => {
                  const meta = CATEGORY_META[category];
                  return (
                    <li key={category}>
                      <Link
                        href={`/resources/${categoryToSlug(category)}${ressourcesSuffix}`}
                        onClick={() => { trackEvent("navbar-ressources-item", { category }); closeMenu(); }}
                        className="flex items-center gap-3 w-full px-4 py-2.5 rounded-xl hover:bg-blue-ulight transition-colors"
                      >
                        <meta.Icon className={`shrink-0 text-base ${meta.color}`} aria-hidden="true" />
                        <div>
                          <p className="text-sm font-semibold text-text-main"><FormattedMessage id={meta.labelKey} /></p>
                          <p className="text-xs text-gray-main"><FormattedMessage id={meta.descKey} /></p>
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          </li>

          <li className="h-px bg-gray-100 mx-2 my-1" />

          <li className="px-2">
            <LanguageSwitcher variant="mobile" onSwitch={closeMenu} />
          </li>
        </ul>

        <div className="p-4 border-t border-gray-100">
          <Link
            href={appUrl}
            onClick={closeMenu}
            onClickCapture={() => { trackEvent("navbar-cta-button", {}, { criticalOnNav: true }); trackCtaClick("navbar-mobile", appTarget); }}
            className="flex items-center justify-center gap-2 w-full bg-blue-main text-white font-semibold px-5 py-3.5 rounded-xl hover:bg-blue-fill transition-colors duration-200"
          >
            <FormattedMessage id="nav.cta" />
            <FaArrowRight aria-hidden="true" className="text-sm" />
          </Link>
        </div>
      </aside>
    </>
  );
};

export default MobileNavbar;
